import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { ProductReference } from '../../../../../core/api/products/entities/product-reference.entity';
import { ProductsApi } from '../../../../../core/api/products/products-api.service';
import { DashboardService } from '../../../../../core/services/dashboard.service';
import { ProductForm } from '../../../../../forms/forms/product.form';
import { formatCurrentStock } from '../../../../../shared/utils/formatters.utils';
import { getProductCategoryLink } from '../../../product-categories/constants/product-category-links.constants';

@Component({
  selector: 'app-product-select',
  templateUrl: './product-select.component.html',
  styleUrl: './product-select.component.scss',
})
export class ProductSelectComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('barcodeInput', { read: ElementRef<HTMLInputElement> }) barcodeInput!: ElementRef<HTMLInputElement>;

  @Input() hideNotInStock = true;

  products: ProductReference[] = [];
  productChangeListener?: Subscription;

  constructor(
    private dashboardService: DashboardService,
    private productsApi: ProductsApi
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.barcodeInput.nativeElement.focus();
      this.barcodeInput.nativeElement.select();
    }, 100);
  }

  ngOnDestroy(): void {
    this.productChangeListener?.unsubscribe();
  }

  get currentStock(): string | null {
    const product = this.dashboardService.product;

    if (product) {
      return formatCurrentStock(product.activeStockItems, product.quantityUnitStock);
    } else return null;
  }

  get form(): ProductForm {
    return this.dashboardService.forms.product;
  }

  get isLoading(): boolean {
    return this.dashboardService.state.isLoading;
  }

  get productSelected(): boolean {
    return !!this.dashboardService.product;
  }

  clearProduct(): void {
    this.dashboardService.clearSelectedProduct();

    setTimeout(() => {
      this.barcodeInput.nativeElement.click();
      this.barcodeInput.nativeElement.focus();
    }, 100);
  }

  openProductPage(event: PointerEvent): void {
    const product = this.dashboardService.product;

    if (product && event.pointerId === 1) {
      window.open(getProductCategoryLink(product.productCategoryId), '_blank');
    }
  }

  refreshProductOptions(): void {
    this.loadData();
  }

  private loadData(): void {
    this.productsApi.findAllCompact().subscribe(products => {
      this.products = products;
      this.form.options.products = products;
      this.setBarcodeChangeListener();
      this.setProductChangeListener();
      this.setSubmissionListener();
    });
  }

  private setBarcodeChangeListener(): void {
    this.dashboardService.forms.product.controls.barcode.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(barcode => {
        if (this.dashboardService.state.isLoading) return;

        if (barcode) {
          this.dashboardService.findProductByBarcode(barcode);
        } else this.dashboardService.clearSelectedProduct();
      });
  }

  private setProductChangeListener(): void {
    this.productChangeListener = this.form.controls.product.valueChanges.pipe(debounceTime(300)).subscribe(name => {
      const dashboardProduct = this.dashboardService.product;
      const dashboardProductName = `${dashboardProduct?.productCategoryName} - ${dashboardProduct?.name}`;

      if (this.dashboardService.state.isLoading || name === dashboardProductName) return;

      if (!name) {
        this.form.options.products = this.products;

        this.dashboardService.forms.shopping.item.clear();

        if (this.dashboardService.forms.product.barcodeExists) {
          this.form.barcodeExists = false;
          this.form.controls.barcode.setValue('');
        }
      } else {
        this.form.options.products = this.products.filter(p => p.name.toLowerCase().includes(name.toLowerCase()));

        const exactMatches = this.form.options.products.filter(p => p.name.toLowerCase() === name.toLowerCase());

        if (exactMatches.length === 1) {
          const product = exactMatches[0];
          this.dashboardService.findProductByName(product.id);
        }
      }
    });
  }

  private setSubmissionListener(): void {
    this.dashboardService.submission.subscribe(() => {
      this.barcodeInput.nativeElement.focus();
      this.barcodeInput.nativeElement.select();
    });
  }
}
